/**
 * FlashingBuilder embed for WordPress product pages.
 * Mounts inside #mighty-flashing-builder-root. Reads product config from data attributes or window.MightyFlashingConfig.
 * On save, calls window.MightyAddCustomFlashingToCart(config, totalExVat, productId, productName) if defined.
 */
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { FlashingBuilder, type FlashingConfig } from '@/components/FlashingBuilder';
import '@/index.css';

const rootId = 'mighty-flashing-builder-root';
const container = document.getElementById(rootId);

if (!container) {
  console.error('[Mighty Flashing Builder] Mount element #mighty-flashing-builder-root not found');
} else {
  const data = container.dataset;
  const productId = data.productId || '';
  const productName = data.productName || 'Custom Flashing';
  const plastisolPrice = parseFloat(data.plastisolPrice || '60');
  const polyesterPrice = parseFloat(data.polyesterPrice || '50');
  const galvanisedPrice = parseFloat(data.galvanisedPrice || '50');

  let initialConfig: Partial<FlashingConfig> | undefined = (() => {
    try {
      const cfg = (window as any).MightyFlashingConfig;
      if (cfg && typeof cfg === 'object') return cfg;
    } catch (_) {}
    return undefined;
  })();

  if (initialConfig) {
    initialConfig.plastisolPrice = initialConfig.plastisolPrice ?? plastisolPrice;
    initialConfig.polyesterPrice = initialConfig.polyesterPrice ?? polyesterPrice;
    initialConfig.galvanisedSteelPrice = initialConfig.galvanisedSteelPrice ?? galvanisedPrice;
  } else {
    initialConfig = {
      plastisolPrice,
      polyesterPrice,
      galvanisedSteelPrice: galvanisedPrice,
    };
  }

  const SHEET_WIDTH = 1220;
  const calculateTotalExVat = (config: FlashingConfig): number => {
    const baseWidth = config.segments.reduce((sum, s) => {
      if (s.type === 'line' && s.length) return sum + s.length;
      return sum;
    }, 0);
    const weltWidth = (config.weltStart ? 15 : 0) + (config.weltEnd ? 15 : 0);
    const totalWidth = baseWidth + weltWidth;
    const unitsPerSheet = totalWidth > 0 ? Math.floor(SHEET_WIDTH / totalWidth) : 0;
    if (unitsPerSheet === 0) return 0;
    let pricePerSheet = 0;
    if (config.variation === 'plastisol') pricePerSheet = config.plastisolPrice;
    else if (config.variation === 'polyester') pricePerSheet = config.polyesterPrice;
    else pricePerSheet = config.galvanisedSteelPrice;
    const pricePerUnit = pricePerSheet / unitsPerSheet;
    return pricePerUnit * config.quantity;
  };

  createRoot(container).render(
    <ThemeProvider defaultTheme="light">
      <div className="w-full h-full bg-background">
        <FlashingBuilder
          config={initialConfig as FlashingConfig}
          readOnlyPrices={true}
          onSave={(config) => {
            const totalExVat = calculateTotalExVat(config);
            const cb = (window as any).MightyAddCustomFlashingToCart;
            if (typeof cb === 'function') {
              cb(config, totalExVat, productId, productName);
            } else {
              console.warn('[Mighty Flashing Builder] MightyAddCustomFlashingToCart not defined. Config:', config, 'Total ex VAT:', totalExVat);
            }
          }}
          onCancel={() => {
            const cb = (window as any).MightyOnFlashingCancel;
            if (typeof cb === 'function') cb();
          }}
        />
      </div>
    </ThemeProvider>
  );
}
